"use client";

import { useState } from "react";
import { PageContainer } from "@/components/common/Container";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  SortableHeader,
} from "@/components/DatatableComponent";
import { ColumnDef } from "@tanstack/react-table";
import { Search, ChevronDown, ChevronUp, ChevronRight, ChevronLeft } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

interface ProcessTableData {
  id: string;
  no: number;
  lot: string;
  qty: string;
  sourceProcessId?: string; // 원래 공정 ID (회수용)
  originalId?: string; // 원래 행 ID (회수용)
}

// 공정별 테이블용 컬럼 정의 함수 (체크박스 오른쪽)
const getProcessTableColumns = (
  selectedRows?: Set<string>,
  onRowSelectionChange?: (rowId: string, selected: boolean) => void
): ColumnDef<ProcessTableData>[] => [
  {
    accessorKey: "no",
    header: ({ column }) => (
      <div className="text-center">
        <SortableHeader column={column}>no</SortableHeader>
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-center font-medium border-r">{row.getValue("no")}</div>
    ),
  },
  {
    accessorKey: "lot",
    header: ({ column }) => (
      <div className="text-center">
        <SortableHeader column={column}>LOT</SortableHeader>
      </div>
    ),
    cell: ({ row }) => {
      const lot = row.getValue("lot") as string;
      return (
        <div className="font-mono text-xs border-r">
          {lot || <span className="text-muted-foreground">-</span>}
        </div>
      );
    },
  },
  {
    accessorKey: "qty",
    header: ({ column }) => (
      <div className="text-center">
        <SortableHeader column={column}>Qty</SortableHeader>
      </div>
    ),
    cell: ({ row }) => {
      const qty = row.getValue("qty") as string;
      return (
        <div className="text-center font-medium">
          {qty || <span className="text-muted-foreground">-</span>}
        </div>
      );
    },
  },
  {
    id: "select",
    header: ({ table }) => (
      <div className="text-center">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      </div>
    ),
    cell: ({ row }) => {
      const isSelected = selectedRows ? selectedRows.has(row.original.id) : row.getIsSelected();
      return (
        <div className="text-center">
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => {
              if (onRowSelectionChange) {
                onRowSelectionChange(row.original.id, !!value);
              } else {
                row.toggleSelected(!!value);
              }
            }}
            aria-label="Select row"
          />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
];

const processes = [
  { id: "raw-material", name: "원자재", code: "00" },
  { id: "forging", name: "단조", code: "01" },
  { id: "rolling", name: "전조", code: "02" },
  { id: "machining", name: "가공", code: "03" },
  { id: "heat-treatment", name: "열처리", code: "04" },
  { id: "surface-treatment", name: "표면처리", code: "05" },
  { id: "packaging", name: "포장", code: "06" },
];

// 드래그 앤 드롭 가능한 테이블 컴포넌트
function ProcessTableWithDragDrop({
  columns,
  data,
  processId,
  onDragStart,
  onDrop,
  isSourceOnly = false,
  movedRowIds,
}: {
  columns: ColumnDef<ProcessTableData>[];
  data: ProcessTableData[];
  processId: string;
  onDragStart: (processId: string, rowId: string) => void;
  onDrop: (targetProcessId: string, targetRowId?: string) => void;
  isSourceOnly?: boolean;
  movedRowIds?: Set<string>;
}) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add("bg-muted/50");
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove("bg-muted/50");
  };

  return (
    <div className="rounded-md overflow-hidden border-0 bg-transparent">
      <div className="overflow-auto relative">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="h-10 bg-muted/50 dark:bg-muted/30 border-r text-center"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isMoved = movedRowIds?.has(row.original.id) || false;
                return (
                  <TableRow
                    key={row.id}
                    draggable={!isMoved}
                    onDragStart={() => {
                      if (!isMoved) {
                        onDragStart(processId, row.original.id);
                      }
                    }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("bg-muted/50");
                    onDrop(processId, row.original.id);
                  }}
                    className={`border-b transition-colors ${
                      isMoved 
                        ? 'bg-muted/30 opacity-50 cursor-not-allowed' 
                        : isSourceOnly 
                          ? 'cursor-move hover:bg-muted/30' 
                          : 'cursor-move hover:bg-muted/30'
                    }`}
                  >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="py-2 border-r"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function MaterialInputPage() {
  const [selectedProcess, setSelectedProcess] = useState<string>("forging"); // 선택한 공정
  const [draggedRow, setDraggedRow] = useState<{ processId: string; rowId: string } | null>(null);
  const [processDataMap, setProcessDataMap] = useState<Record<string, ProcessTableData[]>>({});
  const [selectedProcessData, setSelectedProcessData] = useState<ProcessTableData[]>([]); // 선택한 공정의 데이터
  const [selectedRows, setSelectedRows] = useState<Record<string, Set<string>>>({}); // 각 테이블에서 선택된 행들
  const [selectedProcessRows, setSelectedProcessRows] = useState<Set<string>>(new Set()); // 선택한 공정 테이블에서 선택된 행들
  const [movedRowIds, setMovedRowIds] = useState<Record<string, Set<string>>>({}); // 각 공정에서 이동된 행 ID들 (투입된 항목 추적)

  const initialData: ProcessTableData[] = [
    { id: "1", no: 1, lot: "", qty: "" },
    { id: "2", no: 2, lot: "", qty: "" },
    { id: "3", no: 3, lot: "", qty: "" },
    { id: "4", no: 4, lot: "", qty: "" },
    { id: "5", no: 5, lot: "", qty: "" },
    { id: "6", no: 6, lot: "", qty: "" },
    { id: "7", no: 7, lot: "", qty: "" },
    { id: "8", no: 8, lot: "", qty: "" },
  ];

  const getProcessData = (processId: string, processCode: string): ProcessTableData[] => {
    // 이미 초기화된 데이터가 있으면 사용 (투입된 항목도 표시하되 시각적으로만 표시)
    if (processDataMap[processId]) {
      // 빈 데이터 필터링 (lot과 qty가 모두 비어있으면 제외)
      return processDataMap[processId].filter(row => row.lot || row.qty);
    }

    const baseLot = `251201-${processCode}-001`;
    // 원자재는 8개 행, 단조는 2개 행, 나머지는 기본 6개
    let rowCount = 6;
    if (processId === "raw-material") {
      rowCount = 8;
    } else if (processId === "forging") {
      rowCount = 2;
    }
    
    const data = initialData.slice(0, rowCount).map((row, index) => ({
      ...row,
      id: `${processId}-${row.no}`,
      lot: index === 0 ? baseLot : "",
      qty: index === 0 ? "2,000EA" : "",
    })).filter(row => row.lot || row.qty); // 빈 데이터 제외

    // 초기 데이터 저장
    setProcessDataMap((prev) => ({ ...prev, [processId]: data }));
    return data;
  };

  const handleDragStart = (processId: string, rowId: string) => {
    setDraggedRow({ processId, rowId });
  };

  const handleDrop = (targetProcessId: string, targetRowId?: string) => {
    if (!draggedRow) return;

    const { processId: sourceProcessId, rowId: sourceRowId } = draggedRow;
    
    // 회수: 우측(선택한 공정)에서 좌측(이전 공정)으로 드롭
    if (sourceProcessId === selectedProcess && targetProcessId !== selectedProcess) {
      // 선택한 공정에서 제거할 행 찾기
      const rowToRestore = selectedProcessData.find(item => item.id === sourceRowId);
      if (!rowToRestore || !rowToRestore.sourceProcessId || !rowToRestore.originalId) {
        setDraggedRow(null);
        return;
      }

      // 선택한 공정에서 제거
      setSelectedProcessData((prev) => prev.filter(row => row.id !== sourceRowId));

      // 원래 공정으로 복원 (processDataMap에 추가)
      const { sourceProcessId: restoreProcessId, originalId, ...restRow } = rowToRestore;

      setProcessDataMap((prev) => {
        const existingData = prev[restoreProcessId!] || [];
        const existingIds = new Set(existingData.map(r => r.id));
        if (!existingIds.has(originalId!)) {
          return {
            ...prev,
            [restoreProcessId!]: [...existingData, { ...restRow, id: originalId! }],
          };
        }
        return prev;
      });

      // 이동된 행 ID에서 제거 (회수되었으므로)
      setMovedRowIds((prev) => {
        const newSet = new Set(prev[restoreProcessId!] || []);
        newSet.delete(originalId!);
        return { ...prev, [restoreProcessId!]: newSet };
      });

      setDraggedRow(null);
      return;
    }

    // 투입: 좌측(이전 공정)에서 우측(선택한 공정)으로 드롭
    if (sourceProcessId !== selectedProcess && targetProcessId === selectedProcess) {
      // 이미 투입된 항목인지 체크
      const isAlreadyMoved = movedRowIds[sourceProcessId]?.has(sourceRowId) || false;
      if (isAlreadyMoved) {
        setDraggedRow(null);
        return;
      }

      // 이미 선택한 공정에 같은 originalId를 가진 항목이 있는지 체크
      const alreadyExists = selectedProcessData.some(
        item => item.sourceProcessId === sourceProcessId && item.originalId === sourceRowId
      );
      if (alreadyExists) {
        setDraggedRow(null);
        return;
      }

      // 데이터 가져오기
      const sourceData = [...(processDataMap[sourceProcessId] || getProcessData(sourceProcessId, processes.find(p => p.id === sourceProcessId)?.code || ""))];
      const targetData = [...selectedProcessData];

      const draggedItem = sourceData.find(item => item.id === sourceRowId);
      if (!draggedItem) {
        setDraggedRow(null);
        return;
      }

      // 타겟에 추가 (sourceProcessId와 originalId 저장)
      const timestamp = Date.now();
      const random = Math.random();
      const newRow = {
        ...draggedItem,
        id: `${selectedProcess}-${timestamp}-${random}`,
        sourceProcessId: sourceProcessId,
        originalId: sourceRowId,
      };

      if (targetRowId) {
        const targetIndex = targetData.findIndex(item => item.id === targetRowId);
        if (targetIndex >= 0) {
          targetData.splice(targetIndex, 0, newRow);
        } else {
          targetData.push(newRow);
        }
      } else {
        targetData.push(newRow);
      }

      // 선택한 공정 데이터 업데이트
      setSelectedProcessData(targetData);
      
      // 이동된 행 ID 추적 (제거하지 않고 추적만)
      setMovedRowIds((prev) => {
        const newSet = new Set(prev[sourceProcessId] || []);
        newSet.add(sourceRowId);
        return { ...prev, [sourceProcessId]: newSet };
      });
      
      setDraggedRow(null);
      return;
    }

    setDraggedRow(null);
  };

  // 공정 선택 변경 시 선택한 공정 데이터 초기화
  const handleProcessChange = (processId: string) => {
    setSelectedProcess(processId);
    setSelectedProcessData([]);
    setSelectedRows({});
    setSelectedProcessRows(new Set());
    // movedRowIds는 유지 (이전 공정에서 투입된 항목 추적 유지)
  };

  // 선택한 공정의 바로 이전 공정만 가져오기
  const getPreviousProcesses = (processId: string): string[] => {
    const processIndex = processes.findIndex(p => p.id === processId);
    if (processIndex === -1) return [];
    
    // 첫 공정(단조)이면 원자재만
    if (processIndex === 1) {
      return ["raw-material"];
    } else if (processIndex > 1) {
      // 바로 이전 공정만 반환
      return [processes[processIndex - 1].id];
    }
    return [];
  };

  const previousProcesses = getPreviousProcesses(selectedProcess);

  // 화살표 버튼으로 이동 (좌→우, 투입)
  const handleMoveToSelectedProcess = () => {
    // 좌측 테이블들에서 선택된 행들을 가져와서 우측으로 이동
    const rowsToMove: ProcessTableData[] = [];
    
    previousProcesses.forEach((processId) => {
      const selectedRowIds = selectedRows[processId] || new Set();
      const processData = processDataMap[processId] || getProcessData(processId, processes.find(p => p.id === processId)?.code || "");
      
      selectedRowIds.forEach((rowId) => {
        // 이미 투입된 항목인지 체크
        const isAlreadyMoved = movedRowIds[processId]?.has(rowId) || false;
        if (isAlreadyMoved) return;

        // 이미 선택한 공정에 같은 originalId를 가진 항목이 있는지 체크
        const alreadyExists = selectedProcessData.some(
          item => item.sourceProcessId === processId && item.originalId === rowId
        );
        if (alreadyExists) return;

        const row = processData.find(item => item.id === rowId);
        if (row) {
          rowsToMove.push({ 
            ...row, 
            id: `${selectedProcess}-${Date.now()}-${Math.random()}`,
            sourceProcessId: processId, // 원래 공정 ID 저장
            originalId: row.id // 원래 행 ID 저장
          });
          
          // 이동된 행 ID 추적 (제거하지 않고 추적만)
          setMovedRowIds((prev) => {
            const newSet = new Set(prev[processId] || []);
            newSet.add(rowId);
            return { ...prev, [processId]: newSet };
          });
        }
      });
    });

    if (rowsToMove.length > 0) {
      // 우측에 추가
      setSelectedProcessData((prev) => [...prev, ...rowsToMove]);
      
      // 선택 해제
      setSelectedRows({});
    }
  };

  // 화살표 버튼으로 회수 (우→좌)
  const handleMoveFromSelectedProcess = () => {
    if (selectedProcessRows.size === 0) return;

    // 선택된 행들을 원래 공정으로 돌려보내기
    const rowsToRemove: string[] = [];
    const rowsToRestore: Record<string, string[]> = {}; // 원래 ID들만 저장

    selectedProcessData.forEach((row) => {
      if (selectedProcessRows.has(row.id)) {
        rowsToRemove.push(row.id);
        
        // 원래 공정이 있으면 해당 공정의 이동 추적에서 제거
        if (row.sourceProcessId && row.originalId) {
          if (!rowsToRestore[row.sourceProcessId]) {
            rowsToRestore[row.sourceProcessId] = [];
          }
          rowsToRestore[row.sourceProcessId].push(row.originalId);
        }
      }
    });

    // 선택한 공정에서 제거
    setSelectedProcessData((prev) => prev.filter(row => !rowsToRemove.includes(row.id)));

    // 이동된 행 ID에서 제거 (회수되었으므로 다시 표시됨)
    Object.entries(rowsToRestore).forEach(([processId, originalIds]) => {
      setMovedRowIds((prev) => {
        const newSet = new Set(prev[processId] || []);
        originalIds.forEach(id => {
          newSet.delete(id); // 원래 ID로 제거
        });
        return { ...prev, [processId]: newSet };
      });
    });

    // 선택 해제
    setSelectedProcessRows(new Set());
  };

  return (
    <PageContainer>
      <main className="flex-1 min-h-0 overflow-hidden p-4">
        <div className="container mx-auto flex-1 min-h-0 flex flex-col gap-4">
          {/* 생산 계획 정보 섹션 */}
          <div className="bg-white dark:bg-card rounded-lg border p-4 shrink-0">
            <h2 className="text-lg font-semibold mb-3 pb-2 border-b">
              생산계획번호
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="plan-number" className="text-sm font-medium">
                  생산계획번호
                </Label>
                <div className="relative">
                  <Input
                    id="plan-number"
                    value="2512-0001"
                    readOnly
                    className="pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="unique-number" className="text-sm font-medium">
                  고유번호
                </Label>
                <Input id="unique-number" value="0001" readOnly />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="part-number" className="text-sm font-medium">
                  품번
                </Label>
                <Input id="part-number" value="A002-1-0001" readOnly />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="part-name" className="text-sm font-medium">
                  품명
                </Label>
                <Input id="part-name" value="Spindle Nut" readOnly />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="specification" className="text-sm font-medium">
                  제품규격
                </Label>
                <Input id="specification" value="12*23P" readOnly />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="planned-qty" className="text-sm font-medium">
                  계획량
                </Label>
                <Input id="planned-qty" value="100,000 EA" readOnly />
              </div>
            </div>
          </div>

          {/* 공정 선택 섹션 */}
          <div className="bg-white dark:bg-card rounded-lg border p-4 shrink-0">
            <h2 className="text-lg font-semibold mb-3 pb-2 border-b">
              공정 선택
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              {processes.map((process) => (
                <div key={process.id} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    id={process.id}
                    name="process"
                    checked={selectedProcess === process.id}
                    onChange={() => handleProcessChange(process.id)}
                    className="h-4 w-4"
                  />
                  <Label
                    htmlFor={process.id}
                    className="font-medium cursor-pointer"
                  >
                    {process.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* 공정 테이블 및 폼 섹션 */}
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4">
            {/* 이전 공정 테이블들 (왼쪽) */}
            <div className="flex-1 min-h-0 flex flex-col gap-4">
              {previousProcesses.map((processId) => {
                const process = processes.find(p => p.id === processId);
                if (!process) return null;
                // 현재 공정에 해당하는 데이터만 가져오기 (누적 방지)
                const allData = processDataMap[processId] || getProcessData(processId, process.code);
                const processData = allData.filter(row => {
                  // 빈 데이터 제외
                  if (!row.lot && !row.qty) return false;
                  // 해당 공정의 데이터만 (id가 해당 공정으로 시작하는 것만)
                  return row.id.startsWith(`${processId}-`);
                });
                return (
                  <div key={process.id} className="flex-1 min-h-0 bg-white dark:bg-card rounded-lg border flex flex-col">
                    <div className="px-4 pt-4 pb-3 border-b shrink-0">
                      <h2 className="text-lg font-semibold">
                        {process.name}
                      </h2>
                    </div>
                    <div className="p-0 flex-1 min-h-0 overflow-auto">
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleDrop(process.id);
                        }}
                      >
                        <ProcessTableWithDragDrop
                          columns={getProcessTableColumns(
                            selectedRows[processId] || new Set(),
                            (rowId, selected) => {
                              setSelectedRows((prev) => {
                                const newSet = new Set(prev[processId] || []);
                                if (selected) {
                                  newSet.add(rowId);
                                } else {
                                  newSet.delete(rowId);
                                }
                                return { ...prev, [processId]: newSet };
                              });
                            }
                          )}
                          data={processData}
                          processId={process.id}
                          onDragStart={handleDragStart}
                          onDrop={handleDrop}
                          isSourceOnly={false}
                          movedRowIds={movedRowIds[processId]}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 화살표 버튼들 (모바일: 가로, 데스크톱: 세로) */}
            <div className="flex flex-row lg:flex-col items-center justify-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={handleMoveToSelectedProcess}
                className="h-12 w-12"
                disabled={previousProcesses.every(pId => !selectedRows[pId] || selectedRows[pId].size === 0)}
                aria-label="선택한 항목 이동"
              >
                <ChevronDown className="h-6 w-6 lg:hidden" />
                <ChevronRight className="h-6 w-6 hidden lg:block" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleMoveFromSelectedProcess}
                className="h-12 w-12"
                disabled={selectedProcessRows.size === 0}
                aria-label="선택한 항목 회수"
              >
                <ChevronUp className="h-6 w-6 lg:hidden" />
                <ChevronLeft className="h-6 w-6 hidden lg:block" />
              </Button>
            </div>

            {/* 선택한 공정 테이블 (중앙) */}
            <div className="flex-1 min-h-0 bg-white dark:bg-card rounded-lg border flex flex-col">
              <div className="px-4 pt-4 pb-3 border-b shrink-0">
                <h2 className="text-lg font-semibold">
                  {processes.find(p => p.id === selectedProcess)?.name || "선택한 공정"}
                </h2>
              </div>
              <div className="p-0 flex-1 min-h-0 overflow-auto">
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleDrop(selectedProcess);
                  }}
                  className="h-full"
                >
                  <ProcessTableWithDragDrop
                    columns={getProcessTableColumns(
                      selectedProcessRows,
                      (rowId, selected) => {
                        setSelectedProcessRows((prev) => {
                          const newSet = new Set(prev);
                          if (selected) {
                            newSet.add(rowId);
                          } else {
                            newSet.delete(rowId);
                          }
                          return newSet;
                        });
                      }
                    )}
                    data={selectedProcessData.filter(row => row.lot || row.qty)}
                    processId={selectedProcess}
                    onDragStart={handleDragStart}
                    onDrop={handleDrop}
                    isSourceOnly={false}
                  />
                </div>
              </div>
            </div>

            {/* 폼 섹션 (오른쪽) */}
            <div className="w-full lg:w-[400px] shrink-0 bg-white dark:bg-card rounded-lg border p-4 flex flex-col">
              <h2 className="text-lg font-semibold mb-3 pb-2 border-b">
                추가정보
              </h2>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="work-company" className="text-sm font-medium">
                    작업업체
                  </Label>
                  <div className="relative">
                    <Input
                      id="work-company"
                      value="(주)영완"
                      readOnly
                      className="pr-10"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="work-equipment" className="text-sm font-medium">
                    작업설비
                  </Label>
                  <Input id="work-equipment" value="CNC-01" readOnly />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="registrant-name" className="text-sm font-medium">
                    등록자명
                  </Label>
                  <Input id="registrant-name" value="황순관" readOnly />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="registration-datetime" className="text-sm font-medium">
                    등록일시
                  </Label>
                  <Input id="registration-datetime" value="2025-12-01 09:45" readOnly />
                </div>

                {/* 저장 버튼 */}
                <div className="flex justify-end pt-2">
                  <Button className="bg-brand-600 hover:bg-brand-700 text-white px-8">
                    저장하기
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PageContainer>
  );
}
